import { CalendarRange } from "lucide-react";
import { useTranslation } from "react-i18next";

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { isPeriod, type Period, periodKey, PERIODS } from "@/lib/period";
import { cn } from "@/lib/utils";

interface PeriodSelectProps {
  value: Period;
  onChange: (period: Period) => void;
  className?: string;
}

/**
 * The period of a page of statistics, as a dropdown in its filter bar (the
 * dashboard's own, never the browser's): Afgelopen week, Afgelopen maand,
 * Afgelopen kwartaal, the past 7, 30 or 90 calendar days in Belgian time.
 * Keep it in the URL with `usePeriod` (`@/lib/period`):
 *
 *     const [days, setDays] = usePeriod();
 *     <FilterBar columns={["medium"]} filtered={false} onReset={reset}>
 *       <PeriodSelect value={days} onChange={setDays} />
 *     </FilterBar>
 */
export function PeriodSelect({ value, onChange, className }: PeriodSelectProps) {
  const { t } = useTranslation();
  return (
    <Select
      value={String(value)}
      onValueChange={(next) => {
        const days = Number(next);
        if (isPeriod(days)) {
          onChange(days);
        }
      }}
    >
      <SelectTrigger size="sm" aria-label={t("period.label")} className={cn("w-full", className)}>
        <span className="flex min-w-0 items-center gap-2">
          <CalendarRange aria-hidden="true" className="text-muted size-4" />
          {/* The label as children, so it shows before the list ever opened. */}
          <span className="min-w-0 truncate">
            <SelectValue>{t(`period.last.${periodKey(value)}`)}</SelectValue>
          </span>
        </span>
      </SelectTrigger>
      <SelectContent>
        {PERIODS.map((period) => (
          <SelectItem key={period} value={String(period)}>
            {t(`period.last.${periodKey(period)}`)}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}
